import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * OnlinePresenceContext
 *  - Sends a heartbeat to /api/auth/heartbeat every 30s while the user is logged in and tab is visible.
 *  - Fetches /api/users/online every 20s to know which OTHER users are currently online.
 *  - Exposes `onlineUserIds: Set<string>`, `onlinePersonIds: Set<string>`, `isUserOnline(id)`, `isPersonOnline(id)`.
 */

const OnlineCtx = createContext({
  onlineUserIds: new Set(),
  onlinePersonIds: new Set(),
  isUserOnline: () => false,
  isPersonOnline: () => false,
  details: {},
});

const HEARTBEAT_MS = 30_000;
const POLL_MS = 20_000;

export function OnlinePresenceProvider({ children }) {
  const { user } = useAuth();
  const [details, setDetails] = useState({});
  const hbTimer = useRef(null);
  const pollTimer = useRef(null);

  const sendHeartbeat = async () => {
    if (!user || document.visibilityState === "hidden") return;
    try { await api.post("/auth/heartbeat", {}); } catch { /* ignore */ }
  };

  const pollOnline = async () => {
    if (!user) return;
    try {
      const r = await api.get("/users/online");
      setDetails(r.data || {});
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!user) {
      setDetails({});
      return undefined;
    }
    sendHeartbeat();
    pollOnline();
    hbTimer.current = setInterval(sendHeartbeat, HEARTBEAT_MS);
    pollTimer.current = setInterval(pollOnline, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        sendHeartbeat();
        pollOnline();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(hbTimer.current);
      clearInterval(pollTimer.current);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const onlineUserIds = new Set(Object.keys(details));
  const onlinePersonIds = new Set(
    Object.values(details).map((d) => d.person_id).filter(Boolean)
  );

  const value = {
    onlineUserIds,
    onlinePersonIds,
    isUserOnline: (id) => onlineUserIds.has(id),
    isPersonOnline: (id) => onlinePersonIds.has(id),
    details,
  };
  return <OnlineCtx.Provider value={value}>{children}</OnlineCtx.Provider>;
}

export const useOnlinePresence = () => useContext(OnlineCtx);
